import React, { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import mqtt, { MqttClient } from "mqtt";
import {
  XMarkIcon,
  ArrowPathIcon,
  SignalIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import UseToast from "utils/AfToast";

// MQTT Config
const MQTT_BROKER = process.env.REACT_APP_MQTT_BROKER_URL || "ws://20.244.38.66:9001";
const MQTT_USER = process.env.REACT_APP_MQTT_USERNAME || "airfield_admin";
const MQTT_PASS = process.env.REACT_APP_MQTT_PASSWORD || "Blackmail@1598753";
const MQTT_TOPIC = "airfield/whatsapp/qr";

// WhatsApp SVG Icon
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.456 5.711 1.456h.005c6.554 0 11.89-5.336 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Tools: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [qrData, setQrData] = useState<string>("");
  const [serverMessage, setServerMessage] = useState<string>("");
  const [mqttStatus, setMqttStatus] = useState<"disconnected" | "connecting" | "connected" | "error">("disconnected");
  const clientRef = useRef<MqttClient | null>(null);

  // Connect to MQTT and listen on topic
  const startMqttListening = () => {
    stopMqttListening();
    setMqttStatus("connecting");
    setQrData("");
    setServerMessage("");

    try {
      const client = mqtt.connect(MQTT_BROKER, {
        username: MQTT_USER,
        password: MQTT_PASS,
        reconnectPeriod: 4000,
        connectTimeout: 8000,
      });

      clientRef.current = client;

      client.on("connect", () => {
        setMqttStatus("connected");
        client.subscribe(MQTT_TOPIC, (err) => {
          if (err) {
            console.error("[MQTT] Subscribe error:", err);
            setMqttStatus("error");
            UseToast("MQTT Subscription failed", "error");
          } else {
            console.log(`[MQTT] Subscribed to ${MQTT_TOPIC}`);
          }
        });
      });

      client.on("message", (topic, payload) => {
        if (topic === MQTT_TOPIC) {
          const raw = payload.toString().trim();
          console.log("MQTT Message:", raw);
          if (!raw) return;

          try {
            const parsed = JSON.parse(raw);
            const qr = parsed.qr || parsed.qrImage || null;

            if (qr) {
              setQrData(qr);
              setServerMessage("");
              UseToast("WhatsApp QR received!", "success");
            } else {
              setQrData("");
              setServerMessage(parsed.message || parsed.status || "Server stopped / No QR available");
            }
          } catch {
            // Not JSON
            if (raw.length > 20 && !raw.includes(" ")) {
              setQrData(raw);
              setServerMessage("");
              UseToast("WhatsApp QR received!", "success");
            } else {
              setQrData("");
              setServerMessage(raw);
            }
          }
        }
      });

      client.on("error", (err) => {
        console.error("[MQTT] Connection error:", err.message);
        setMqttStatus("error");
      });

      client.on("close", () => {
        console.log("[MQTT] Connection closed");
      });
    } catch (err: any) {
      console.error("[MQTT] Setup error:", err);
      setMqttStatus("error");
      UseToast("Failed to initialize MQTT connection", "error");
    }
  };

  // Stop MQTT listening and disconnect
  const stopMqttListening = () => {
    if (clientRef.current) {
      try {
        clientRef.current.unsubscribe(MQTT_TOPIC);
        clientRef.current.end(true);
      } catch (err) {
        console.warn("[MQTT] Error while disconnecting:", err);
      }
      clientRef.current = null;
    }
    setMqttStatus("disconnected");
    setQrData("");
    setServerMessage("");
  };

  // Open popup and begin listening
  const handleOpenPopup = () => {
    setIsPopupOpen(true);
    startMqttListening();
  };

  // Close popup and stop listening
  const handleClosePopup = () => {
    stopMqttListening();
    setIsPopupOpen(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMqttListening();
    };
  }, []);

  return (
    <div className="w-full min-h-[calc(100vh-80px)] p-6 bg-slate-50 font-quicksand">
      {/* Clean full-width toolbar for tool buttons */}
      <div className="flex flex-wrap items-center gap-4 w-full">
        <button
          id="btn-whatsapp-qr"
          type="button"
          onClick={handleOpenPopup}
          className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 shadow-md shadow-emerald-600/20 hover:shadow-lg hover:shadow-emerald-600/30 transition-all cursor-pointer"
        >
          <WhatsAppIcon className="w-5 h-5" />
          <span>Whatsapp QR</span>
        </button>
      </div>

      {/* WhatsApp QR Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={handleClosePopup}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 px-6 py-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <WhatsAppIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-tight">
                    Whatsapp QR
                  </h3>
                  <p className="text-[11px] text-white/80">
                    Channel: {MQTT_TOPIC}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClosePopup}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex flex-col items-center">
              {/* QR Display or Server Message or Listening State */}
              {qrData ? (
                <div className="relative p-4 bg-white rounded-2xl border-2 border-emerald-100 shadow-md shadow-emerald-500/10 mb-4 flex items-center justify-center">
                  {qrData.startsWith("data:image/") || qrData.startsWith("http://") || qrData.startsWith("https://") ? (
                    <img
                      src={qrData}
                      alt="WhatsApp Web QR Code"
                      className="w-[240px] h-[240px] object-contain rounded-lg"
                    />
                  ) : (
                    <div className="bg-white p-2 rounded-lg">
                      <QRCode value={qrData} size={240} level="M" />
                    </div>
                  )}
                </div>
              ) : serverMessage ? (
                <div className="w-[260px] min-h-[220px] rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/60 flex flex-col items-center justify-center p-5 text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
                    <ExclamationTriangleIcon className="w-6 h-6" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100/60 px-2 py-0.5 rounded-full mb-1.5">
                    Server Message
                  </p>
                  <p className="text-sm font-bold text-slate-800 break-words max-w-[220px]">
                    {serverMessage}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-2">
                    Waiting for QR on <span className="font-mono text-emerald-700 text-[10px]">{MQTT_TOPIC}</span>
                  </p>
                </div>
              ) : (
                <div className="w-[260px] h-[260px] rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 flex flex-col items-center justify-center p-4 text-center mb-4">
                  <div className="relative mb-3 flex items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-12 w-12 rounded-full bg-emerald-400 opacity-30" />
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                      <SignalIcon className="w-6 h-6 animate-pulse" />
                    </div>
                  </div>
                  <p className="text-sm font-bold text-slate-800">
                    Waiting for QR Code...
                  </p>
                  <p className="text-xs text-slate-500 mt-1 max-w-[200px]">
                    Listening on <span className="font-mono text-emerald-700 text-[11px]">{MQTT_TOPIC}</span>
                  </p>
                </div>
              )}

              {/* Status Indicator */}
              <div className="flex items-center gap-2 mb-5">
                {mqttStatus === "connected" ? (
                  <>
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                    </span>
                    <span className="text-xs font-bold text-emerald-700">
                      MQTT Connected
                    </span>
                  </>
                ) : mqttStatus === "connecting" ? (
                  <>
                    <ArrowPathIcon className="w-3.5 h-3.5 text-amber-500 animate-spin" />
                    <span className="text-xs font-semibold text-amber-600">
                      Connecting to MQTT...
                    </span>
                  </>
                ) : (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="text-xs font-semibold text-red-600">
                      MQTT Connection Issue
                    </span>
                  </>
                )}
                <span className="text-xs text-slate-300">•</span>
                <span className="text-[11px] text-slate-500">
                  {qrData ? "QR Ready" : serverMessage ? "Status Received" : "Awaiting data"}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="w-full flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={startMqttListening}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 active:scale-95 transition-all cursor-pointer"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  <span>Reconnect</span>
                </button>

                <button
                  type="button"
                  onClick={handleClosePopup}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2.5 rounded-xl text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 active:scale-95 transition-all cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tools;
